import { z } from 'zod';
import { UserTraitsSchema } from './user';

export const IdentifyInputSchema = z.object({
  userInfo: z
    .object({
      user: z.record(z.any()).optional(),
    })
    .passthrough()
    .optional()
    .or(z.record(z.any()).optional()) // Permite también la estructura directa { role, member_status, plan_code }
    .or(z.null()), // Permite también null
  traits: UserTraitsSchema.passthrough().optional(),
});

export const DeviceSchema = z.string().min(1, 'Device must not be empty').optional();

export const IdentifyOutputSchema = z
  .object({
    role: z.string().optional(),
    member_status: z.string().optional(),
    plan_code: z.string().optional(),
    experiment_name: z.string().optional(),
    variation_name: z.string().optional(),
    feature_flag_name: z.string().optional(),
    campaign_id: z.union([z.number(), z.string()]).optional(),
    fff_uuid: z.string().optional(),
    device: DeviceSchema,
    fff_session_id: z.string().optional(),
  })
  .passthrough();

export const IdentifyTransformSchema = IdentifyInputSchema.transform(({ userInfo, traits }) => {
  const getUserRole = () => {
    if (!userInfo) return undefined;
    if (userInfo.user?.['role']) return userInfo.user['role'];
    if (userInfo['role']) return userInfo['role'];
    return undefined;
  };

  const getUserMemberStatus = () => {
    if (!userInfo) return undefined;
    if (userInfo.user?.['subscriptionState']) return userInfo.user['subscriptionState'];
    if (userInfo['member_status']) return userInfo['member_status'];
    return undefined;
  };

  const getUserPlanCode = () => {
    if (!userInfo) return undefined;
    // A *present* plan code is preserved — including `null` (legacy /menu/user-data
    // `MenuData` allows `user.planCode: PlanCode | null` for members with no box
    // plan) and `''` — normalized to `''` so the downstream mapper still emits an
    // empty trait and clears any stale `plan_code` in Segment. Only a truly absent
    // (`undefined`) plan code is skipped.
    const nestedPlanCode = userInfo.user?.['planCode'];
    if (nestedPlanCode !== undefined) return nestedPlanCode ?? '';
    const directPlanCode = userInfo['plan_code'];
    if (directPlanCode !== undefined) return directPlanCode ?? '';
    return undefined;
  };

  return {
    role: traits?.role || getUserRole(),
    member_status: traits?.memberStatus || getUserMemberStatus(),
    plan_code: traits?.planCode || getUserPlanCode(),
    experiment_name: traits?.experimentName,
    variation_name: traits?.variationName,
    feature_flag_name: traits?.featureFlagName,
    campaign_id: traits?.campaignId,
    fff_uuid: traits?.fffUUID,
    fff_session_id: traits?.fff_session_id,
  };
});

export type IdentifyInput = z.infer<typeof IdentifyInputSchema>;
export type IdentifyOutput = z.infer<typeof IdentifyOutputSchema>;
export type Device = z.infer<typeof DeviceSchema>;
